import {Injectable} from '@angular/core';

/** What the windows showing one CRD host tell each other. */
export type CrdWindowMessage =
  /** A window of its own now shows this screen; nobody else may join it. */
  | {type: 'claimed'; monitor: number}
  /** That window closed or reloads. */
  | {type: 'released'; monitor: number}
  /** The main page takes the screen back; its window closes. */
  | {type: 'return'; monitor: number}
  /** A join reset the Windows password. */
  | {type: 'password'; password: string}
  /** Who plays the audio may have changed (`audioWindow`): check again. */
  | {type: 'audio'}
  /** Text was sent to the host's clipboard from that window (CrdLocalClipboard). */
  | {type: 'clipboard'; text: string}
  /** A drag held in screen `from`'s window is past its picture, at this desktop point (CrdStreamComponent.routeDrag). */
  | {type: 'drag'; from: number; screenX: number; screenY: number; buttons: number; attempt: number}
  /** That point is on another window's picture, which moved the host cursor there; `attempt` from the offer. */
  | {type: 'drag-taken'; from: number; attempt: number};

/**
 * Coordinates the browser windows showing one CRD host: the session page and the windows a screen was moved
 * to. A join kills that monitor's running binary, so only one window may hold a screen.
 *
 * Messages go over a BroadcastChannel (same browser and origin). The claims are also kept in localStorage,
 * so a page loaded later knows them synchronously, before it joins anything. A claim left behind by a window
 * that crashed only shows that screen as moved; "Bring back" clears it.
 *
 * The page closes the channel itself (`disconnect`), after its last messages: Angular destroys a component's
 * providers before the component's own DestroyRef callbacks run, so an ngOnDestroy here would come too early.
 */
@Injectable()
export class CrdScreenWindows {
  private channel?: BroadcastChannel;
  private hostId = '';
  /** This page among the host's session pages (its audio entry). */
  private readonly pageId = Math.random().toString(36).slice(2);

  /** Starts listening for the other windows of `hostId`. */
  connect(hostId: string, listener: (message: CrdWindowMessage) => void) {
    this.hostId = hostId;
    if (typeof BroadcastChannel === 'undefined') return;
    this.channel = new BroadcastChannel(`crd-host:${hostId}`);
    this.channel.onmessage = (event) => listener(event.data as CrdWindowMessage);
  }

  /** Whether moving a screen to a window of its own can work in this browser. */
  get supported(): boolean {
    return !!this.channel;
  }

  post(message: CrdWindowMessage) {
    this.channel?.postMessage(message);
  }

  /** Stops listening and sending. Call after the page's last message. */
  disconnect() {
    this.channel?.close();
    this.channel = undefined;
  }

  /** Of `screens`, those a window of their own holds, as far as this browser knows. */
  claimed(screens: number[]): number[] {
    try {
      return screens.filter(m => localStorage.getItem(this.key(m)) !== null);
    } catch {
      return [];
    }
  }

  /**
   * Whether a session page of this host holds a screen, and so plays the audio. Each page keeps its own entry
   * in localStorage (several can be open, e.g. Connect clicked twice), so one page with no screen doesn't
   * clear another's; a window loaded later, or one that outlives the pages, reads them without asking.
   *
   * Accepted limitation: an entry has no expiry. A page that is killed (browser crash, forced tab termination)
   * skips pagehide and Angular's teardown, so its entry stays, and the host's pop-outs then never play audio,
   * even after the session page is opened again. The audio is then heard by keeping a screen open on a session
   * page; clearing the site's data removes the entry. A heartbeat with expiry would fix it, but wasn't worth
   * the extra timers for a crash-only case. A pop-out's claim has the same gap (see above: Bring Back clears it).
   */
  pageHasAudio(): boolean {
    try {
      const prefix = this.key('page-audio:');
      return Array.from({length: localStorage.length}, (_, i) => localStorage.key(i)).some(k => k?.startsWith(prefix));
    } catch {
      return false;
    }
  }

  /** This page's entry: on while it holds a screen. */
  setPageHasAudio(on: boolean) {
    try {
      if (on) localStorage.setItem(this.key(`page-audio:${this.pageId}`), '1');
      else localStorage.removeItem(this.key(`page-audio:${this.pageId}`));
    } catch {
      // No storage: the pop-outs then stay muted and the page plays the audio.
    }
  }

  /**
   * The screen whose own window plays the audio: none while the session page holds a screen, else the lowest
   * claimed one. Every window works it out the same way from storage, so no window has to assign it, and the
   * answer survives the page closing and a pop-out reloading or closing.
   */
  audioWindow(screens: number[]): number | null {
    if (this.pageHasAudio()) return null;
    const claimed = this.claimed(screens);
    return claimed.length ? Math.min(...claimed) : null;
  }

  setClaimed(monitor: number, claimed: boolean) {
    try {
      if (claimed) localStorage.setItem(this.key(monitor), String(Date.now()));
      else localStorage.removeItem(this.key(monitor));
    } catch {
      // No storage (private window, blocked site data): the channel messages still work.
    }
  }

  private key(name: number | string): string {
    return `crd-screen-window:${this.hostId}:${name}`;
  }
}
